import React from "react";
import { useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";

import ProgressTracker from "components/ProgressTracker";
import StepNavigation from "components/StepNavigation";
import FaoBuilderHeader from "./fao-layout/fao-builder-header";
import Loader from "components/partials/loader/loader";

import { STYLES, mergeStyles } from "./constants/styles.constants";
import { useAwardingStrategy } from "./hooks/use-awarding-strategy";
import { formatValue } from "./helpers/awarding-strategy.helpers";
import {
  OUTSIDE_AID_TYPES,
  FUND_TYPES,
} from "./constants/awarding-strategy.constants";

const FaoAwardingStrategy = () => {
  const navigate = useNavigate();

  const {
    isLoading,
    funds,
    outsideAidChecked,
    groupedFunds,
    componentStatus,
    modelConfig,
    handleInputChange,
    handleOutsideAidCheckChange,
    saveAwardingStrategy,
  } = useAwardingStrategy();

  const { isSaved, isSaving } = componentStatus;

  const handleBlur = (e, groupedFund, valueIndex) => {
    const value = e.target.value.replace(/[^0-9.-]+/g, "");
    const numericValue = value ? Number(value) : 0;
    e.target.value = formatValue(numericValue);
  };

  return (
    <div className="flex w-full flex-col">
      <div className="w-full">
        <ToastContainer
          style={{ width: "500px", fontSize: "16px" }}
          progressStyle={{ backgroundColor: "#b6de3a" }}
        />
        <FaoBuilderHeader
          modelName={modelConfig?.versionName}
          modelId={modelConfig?.id}
        />
        <ProgressTracker />

        {isLoading ? (
          <Loader />
        ) : (
          <div className="mt-6 space-y-8">
            {/* Institutional Funds */}
            <div>
              <h2 className="text-lg font-semibold text-blue-03">
                Institutional Funds
              </h2>
              <p className="text-sm text-gray-600">
                Define institutional funds and their stacking behavior.
              </p>
              <div className="mt-2 rounded border border-gray-300">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-2 text-left text-sm font-semibold text-blue-03">
                        Fund Name
                      </th>
                      <th className="px-4 py-2 text-left text-sm font-semibold text-blue-03">
                        2025
                      </th>
                      <th className="px-4 py-2 text-left text-sm font-semibold text-blue-03">
                        2026
                      </th>
                      <th className="px-4 py-2 text-left text-sm font-semibold text-blue-03">
                        Stack or Topper
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupedFunds.map((groupedFund) => (
                      <tr
                        key={groupedFund.name}
                        className="border-t border-gray-300"
                      >
                        <td className="px-4 py-2 align-top text-blue-03">
                          {groupedFund.name}
                        </td>
                        <td className="px-4 py-2 text-blue-03">
                          <div className="flex flex-col gap-2">
                            {groupedFund.values.map((value, valueIndex) => (
                              <div
                                key={valueIndex}
                                className="flex h-9 items-center"
                              >
                                {formatValue(value.year2025)}
                              </div>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-2">
                          <div className="flex flex-col gap-2">
                            {groupedFund.values.map((value, valueIndex) => (
                              <div key={valueIndex}>
                                <input
                                  type="text"
                                  value={formatValue(value.year2026)}
                                  onChange={(e) =>
                                    handleInputChange({
                                      fundName: groupedFund.name,
                                      valueIndex,
                                      field: "year2026",
                                      value: e.target.value,
                                    })
                                  }
                                  onBlur={(e) =>
                                    handleBlur(e, groupedFund, valueIndex)
                                  }
                                  className="h-9 w-full rounded border border-gray-300 px-2 py-1"
                                />
                              </div>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-2 align-top">
                          <select
                            value={groupedFund.type}
                            onChange={(e) =>
                              handleInputChange({
                                fundName: groupedFund.name,
                                valueIndex: 0,
                                field: "type",
                                value: e.target.value,
                              })
                            }
                            className="w-full rounded border border-gray-300 px-2 py-1"
                          >
                            <option value={FUND_TYPES.STACK}>
                              {FUND_TYPES.STACK}
                            </option>
                            <option value={FUND_TYPES.TOPPER}>
                              {FUND_TYPES.TOPPER}
                            </option>
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Outside Aid */}
            <div>
              <h2 className="text-lg font-semibold text-blue-03">
                Outside Aid Stack
              </h2>
              <p className="text-sm text-gray-600">
                Select sources of outside financial aid to include in
                calculations.
              </p>
              <div className="mt-2 grid grid-cols-1 gap-2">
                {OUTSIDE_AID_TYPES.map((aid, index) => (
                  <div key={index} className="flex items-center">
                    <input
                      type="checkbox"
                      id={`aid-${index}`}
                      checked={outsideAidChecked[index] || false}
                      onChange={() => handleOutsideAidCheckChange(index)}
                      className={mergeStyles(STYLES.checkbox.input, "mr-2")}
                    />
                    <label
                      htmlFor={`aid-${index}`}
                      className="font-family text-md flex text-gray-05"
                    >
                      {aid}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <StepNavigation
          onClickBack={() =>
            navigate(`../fao-builder/${modelConfig?.id}/tuition`)
          }
          isSaving={isSaving}
          isSaved={isSaved}
          onClickSave={saveAwardingStrategy}
          onClickNext={() =>
            navigate(`../fao-builder/${modelConfig?.id}/checkout`)
          }
          disabledNext={!isSaved}
        />
      </div>
    </div>
  );
};

export default FaoAwardingStrategy;
